import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Select, TextField } from '@nyce/admin-kit/ui';
import { ListField, Page, useAsync, useInterval } from '../../components/common';
import { TargetShell } from '../../identity/TargetShell';
import { TenantPicker } from '../../identity/TenantPicker';
import { activeTarget, authClientFor, useTargets } from '../../identity/session';
import { automationGw, type AuthMode, type Platform, type RouteDirection } from '../../automation/api/client';
import { SCOPES } from '../../api';
import { formatRelative } from '../../lib/format';
import { targetOf, useOnboardingConfig } from '../api/config';
import { addScopes, missingScopes } from '../api/auth';
import { agentFiles } from '../generate/agent';
import { guidePath } from '../guides';
import { Wizard, type WizardStep } from '../wizard/Wizard';
import { FileOutput } from '../wizard/FileOutput';
import { useWizardParam } from '../wizard/params';
import { PLACEHOLDER, rememberSecret, useSecrets } from '../wizard/secrets';
import { Facts, NeedsScope, NeedsSession, Note, StepActions, useAction } from '../wizard/parts';

const PLATFORMS: { value: Platform; label: string }[] = [
  { value: 'windows', label: 'Windows (service)' },
  { value: 'linux', label: 'Linux (systemd)' },
  { value: 'container', label: 'Container (Docker / Kubernetes)' },
];

const AGENT_ID = /^[A-Za-z0-9][A-Za-z0-9._-]{2,63}$/;

/** W7: gateway tenant (+ AUTH link), an agent with routes, its config file and installer, then AUTH. */
export function AutomationWizardPage() {
  return (
    <Page
      title="Automation agent"
      description="Connect a customer site to the automation gateway: the gateway tenant and its AUTH link, the agent and its routes, the config file and installer for the site, then the agent's move to AUTH."
    >
      <TargetShell service="automation">
        <AutomationWizard />
      </TargetShell>
    </Page>
  );
}

function AutomationWizard() {
  const target = activeTarget();
  const gw = useMemo(() => automationGw(target), [target]);
  const config = useOnboardingConfig();
  const env = targetOf(config, target);
  const { signedIn } = useTargets();
  const session = signedIn(target);
  const secrets = useSecrets();
  const [tenantId, setTenantId] = useWizardParam('gwTenant');
  const [authTenant, setAuthTenant] = useWizardParam('authTenant');
  const [agentId, setAgentId] = useWizardParam('agent');
  const [platformParam, setPlatform] = useWizardParam('platform');
  const [step] = useWizardParam('step');
  const platform = (PLATFORMS.find((p) => p.value === platformParam)?.value ?? 'windows') as Platform;

  const [newTenant, setNewTenant] = useState('');
  const [draft, setDraft] = useState({ clientId: '', displayName: '', ranges: [] as string[] });
  const [route, setRoute] = useState({
    direction: 'forward' as RouteDirection,
    publicPort: '',
    targetHost: '',
    targetPort: '',
    namespaces: [] as string[],
  });

  const state = useAsync(async () => {
    const [tenants, agents, routes, tunnels] = await Promise.all([
      gw.tenants(),
      gw.agents(),
      gw.routes(),
      gw.tunnels().catch(() => []),
    ]);
    const tenant = tenants.find((t) => t.id === tenantId);
    const tid = tenant?.authTenantId ?? null;
    const claims =
      session && tid
        ? await authClientFor(target)
            .tenants.claims.list(tid)
            .catch(() => null)
        : null;
    return { tenants, agents, routes, tunnels, tenant, claims };
  }, [gw, target, tenantId, session]);
  const action = useAction(state.reload);
  const s = state.data;
  const agent = s?.agents.find((a) => a.id === agentId);
  const tunnel = agent && s?.tunnels.find((t) => t.clientId === agent.clientId);
  const connected = !!tunnel?.isConnected;
  const agentRoutes = (s?.routes ?? []).filter((r) => r.clientId === agentId);
  const missing = missingScopes(s?.claims, ['automation-gw:tunnel']);
  const linked = !!s?.tenant?.authTenantId;
  const sk = (k: string) => `agw:${target}:${agentId}:${k}`;
  const secret = secrets(sk('secret'));
  const authId = secrets(sk('auth-id'));
  const authSecret = secrets(sk('auth-secret'));
  const freePort = Math.max(10000, ...(s?.routes ?? []).map((r) => r.publicPort + 1));
  useInterval(() => void state.reload(), 10_000, step === 'install' && !!agent && !connected);

  const files =
    agent && env?.gatewayWsUrl
      ? agentFiles(
          {
            gatewayUrl: env.gatewayWsUrl,
            clientId: agent.clientId,
            token:
              platform === 'container' && (authId || agent.authClientId)
                ? undefined
                : (secret ?? PLACEHOLDER),
            auth:
              platform === 'container' && (authId || agent.authClientId)
                ? {
                    clientId: authId ?? agent.authClientId ?? PLACEHOLDER,
                    clientSecret: authSecret ?? PLACEHOLDER,
                  }
                : undefined,
          },
          platform,
          env.agentImage ?? 'images.nycelogic.com/nyce.logic/automation-gw-agent:latest'
        )
      : [];

  const createContainerAuth = () =>
    action.run('container-auth', async () => {
      const r = await gw.containerAuthClient(agentId);
      rememberSecret(sk('auth-id'), r.authClientId);
      rememberSecret(sk('auth-secret'), r.clientSecret);
    });

  const steps: WizardStep[] = [
    {
      id: 'tenant',
      title: 'Gateway tenant',
      state: s?.tenant ? 'done' : 'todo',
      summary: s?.tenant?.name,
      body: (
        <>
          <p>Agents belong to a tenant of the gateway: one per customer.</p>
          <Select
            label="Tenant"
            value={tenantId}
            onChange={(v) => {
              setTenantId(v);
              setAgentId('');
            }}
            options={[
              { value: '', label: '— pick or create —' },
              ...(s?.tenants ?? []).map((t) => ({ value: t.id, label: t.name })),
            ]}
          />
          <div className="nyk-form">
            <TextField
              label="New tenant"
              value={newTenant}
              onChange={(e) => setNewTenant(e.target.value)}
              hint="The customer's name (at least 3 characters)."
            />
          </div>
          <StepActions error={action.error}>
            <Button
              disabled={newTenant.trim().length < 3}
              loading={action.busy === 'tenant'}
              onClick={() =>
                void action.run('tenant', async () => {
                  const t = await gw.createTenant(newTenant.trim());
                  setTenantId(t.id);
                  setNewTenant('');
                })
              }
            >
              Create tenant
            </Button>
          </StepActions>
        </>
      ),
    },
    {
      id: 'auth',
      title: 'Link to AUTH',
      state: !s?.tenant ? 'blocked' : linked && !missing.length ? 'done' : 'todo',
      blocked: 'Pick the gateway tenant first.',
      summary: linked ? 'linked' : undefined,
      body: (
        <NeedsSession target={target}>
          <p>
            Agents move to AUTH with a client in the customer's AUTH tenant (scope{' '}
            <code>automation-gw:tunnel</code>). Link the gateway tenant to it.
          </p>
          {linked ? (
            <Facts
              rows={[
                ['AUTH tenant', <code key="t">{s?.tenant?.authTenantId}</code>],
                ['Missing scopes', missing.length ? missing.join(', ') : 'none'],
              ]}
            />
          ) : (
            <TenantPicker target={target} value={authTenant} onChange={setAuthTenant} allowNone={false} />
          )}
          <StepActions error={action.error}>
            {!linked && (
              <Button
                disabled={!authTenant}
                loading={action.busy === 'link'}
                onClick={() =>
                  void action.run('link', async () => {
                    await gw.updateTenant(tenantId, { name: s!.tenant!.name, authTenantId: authTenant });
                    await addScopes(target, authTenant, ['automation-gw:tunnel']);
                  })
                }
              >
                Link and add scope
              </Button>
            )}
            {linked && missing.length > 0 && (
              <NeedsScope scope={SCOPES.authWrite}>
                <Button
                  loading={action.busy === 'scope'}
                  onClick={() =>
                    void action.run('scope', () => addScopes(target, s!.tenant!.authTenantId!, missing))
                  }
                >
                  Add automation-gw:tunnel
                </Button>
              </NeedsScope>
            )}
          </StepActions>
        </NeedsSession>
      ),
    },
    {
      id: 'agent',
      title: 'Agent',
      state: !s?.tenant ? 'blocked' : agent ? 'done' : 'todo',
      blocked: 'Pick the gateway tenant first.',
      summary: agent
        ? `${agent.clientId} · ${PLATFORMS.find((p) => p.value === platform)?.label}`
        : undefined,
      body: (
        <>
          <Select label="Runs on" value={platform} onChange={setPlatform} options={PLATFORMS} />
          {agent ? (
            <>
              <Facts
                rows={[
                  ['Agent', `${agent.displayName} (${agent.clientId})`],
                  ['Secret', secret ? 'issued now (in the config file)' : agent.hasSecret ? 'set' : 'none'],
                  ['AUTH client', authId ?? agent.authClientId ?? 'none'],
                ]}
              />
              <StepActions error={action.error}>
                <Button
                  variant="ghost"
                  loading={action.busy === 'rotate'}
                  onClick={() =>
                    void action.run('rotate', async () => {
                      const r = await gw.rotateSecret(agentId);
                      rememberSecret(sk('secret'), r.sharedSecret);
                    })
                  }
                >
                  New secret
                </Button>
                {platform === 'container' && (
                  <Button
                    disabled={!linked || !session}
                    title={!linked ? 'Link the tenant to AUTH first.' : undefined}
                    loading={action.busy === 'container-auth'}
                    onClick={() => void createContainerAuth()}
                  >
                    {authSecret ? 'New AUTH secret' : 'Create AUTH client'}
                  </Button>
                )}
                <Button variant="ghost" onClick={() => setAgentId('')}>
                  Another agent
                </Button>
              </StepActions>
            </>
          ) : (
            <>
              <Select
                label="Existing agent"
                value=""
                onChange={setAgentId}
                options={[
                  { value: '', label: '— or create one below —' },
                  ...(s?.agents ?? [])
                    .filter((a) => a.tenantId === tenantId)
                    .map((a) => ({ value: a.id, label: `${a.displayName} (${a.clientId})` })),
                ]}
              />
              <div className="nyk-form">
                <TextField
                  label="Agent id"
                  value={draft.clientId}
                  onChange={(e) => setDraft({ ...draft, clientId: e.target.value.trim() })}
                  hint="What the agent connects with, e.g. acme-dc1 (3–64: letters, digits, . _ -)."
                  error={
                    draft.clientId && !AGENT_ID.test(draft.clientId)
                      ? 'letters, digits, . _ - (3–64)'
                      : undefined
                  }
                />
                <TextField
                  label="Display name"
                  value={draft.displayName}
                  onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
                />
                <div className="nyk-span2">
                  <ListField
                    label="Allowed IP ranges"
                    value={draft.ranges}
                    onChange={(ranges) => setDraft({ ...draft, ranges })}
                    hint="The site's public addresses (IP or CIDR); empty = anywhere."
                  />
                </div>
              </div>
              <StepActions error={action.error}>
                <Button
                  disabled={!AGENT_ID.test(draft.clientId) || !draft.displayName.trim()}
                  loading={action.busy === 'agent'}
                  onClick={() =>
                    void action.run('agent', async () => {
                      const a = await gw.createAgent({
                        tenantId,
                        clientId: draft.clientId,
                        displayName: draft.displayName.trim(),
                        allowedIpRanges: draft.ranges,
                      });
                      if (a.sharedSecret) rememberSecret(`agw:${target}:${a.id}:secret`, a.sharedSecret);
                      setAgentId(a.id);
                    })
                  }
                >
                  Create agent
                </Button>
              </StepActions>
            </>
          )}
        </>
      ),
    },
    {
      id: 'routes',
      title: 'Routes',
      state: !agent ? 'blocked' : agentRoutes.length ? 'done' : 'optional',
      blocked: 'Create the agent first.',
      summary: agentRoutes.length ? `${agentRoutes.length} route(s)` : undefined,
      body: (
        <>
          <p>
            A <b>forward</b> route lets the cloud reach a host on site (public port on the gateway → target
            host:port on the site); a <b>reverse</b> route the other way. Agents receive their routes when
            they connect. File watchers are set up on the{' '}
            <Link className="nyk-link" to={`/automation/file-watchers?target=${target}`}>
              File watchers
            </Link>{' '}
            page.
          </p>
          {!!agentRoutes.length && (
            <Facts
              rows={agentRoutes.map((r) => [
                `${r.direction} :${r.publicPort}`,
                `${r.targetHost}:${r.targetPort}${r.allowedNamespaces.length ? ` (${r.allowedNamespaces.join(', ')})` : ''}`,
              ])}
            />
          )}
          <div className="nyk-form">
            <Select
              label="Direction"
              value={route.direction}
              onChange={(v) => setRoute({ ...route, direction: v as RouteDirection })}
              options={[
                { value: 'forward', label: 'Forward (cloud → site)' },
                { value: 'reverse', label: 'Reverse (site → cloud)' },
              ]}
            />
            <TextField
              label="Public port"
              inputMode="numeric"
              value={route.publicPort}
              placeholder={String(freePort)}
              onChange={(e) => setRoute({ ...route, publicPort: e.target.value.replace(/\D/g, '') })}
              hint={`Free from ${freePort}.`}
            />
            <TextField
              label="Target host"
              value={route.targetHost}
              onChange={(e) => setRoute({ ...route, targetHost: e.target.value.trim() })}
            />
            <TextField
              label="Target port"
              inputMode="numeric"
              value={route.targetPort}
              onChange={(e) => setRoute({ ...route, targetPort: e.target.value.replace(/\D/g, '') })}
            />
            <div className="nyk-span2">
              <ListField
                label="Allowed namespaces"
                value={route.namespaces}
                onChange={(namespaces) => setRoute({ ...route, namespaces })}
                hint="Kubernetes namespaces that may use a forward route, e.g. the installation's."
              />
            </div>
          </div>
          <StepActions error={action.error}>
            <Button
              variant="secondary"
              disabled={!route.targetHost || !route.targetPort}
              loading={action.busy === 'route'}
              onClick={() =>
                void action.run('route', async () => {
                  const port = Number(route.publicPort || freePort);
                  if ((s?.routes ?? []).some((r) => r.publicPort === port))
                    throw new Error(`Public port ${port} is taken.`);
                  await gw.createRoute({
                    clientId: agentId,
                    direction: route.direction,
                    publicPort: port,
                    targetHost: route.targetHost,
                    targetPort: Number(route.targetPort),
                    enabled: true,
                    allowedNamespaces: route.namespaces,
                    allowedSourceIPs: [],
                  });
                  setRoute({ ...route, publicPort: '', targetHost: '', targetPort: '' });
                })
              }
            >
              Add route
            </Button>
          </StepActions>
        </>
      ),
    },
    {
      id: 'install',
      title: 'Install on site',
      state: !agent ? 'blocked' : connected ? 'done' : 'waiting',
      blocked: 'Create the agent first.',
      summary: connected
        ? `connected ${tunnel?.connectedAt ? formatRelative(tunnel.connectedAt) : ''}`
        : undefined,
      body: env?.gatewayWsUrl ? (
        <>
          {platform === 'container' && !agent?.authClientId && !authId && (
            <Note tone="warn">
              A container agent signs in with its own AUTH client: create it in the Agent step first (the
              files fall back to the shared secret meanwhile).
            </Note>
          )}
          <p>
            {platform !== 'container' && env.agentBundleUrl && (
              <>
                Download:{' '}
                <a
                  className="nyk-link"
                  href={env.agentBundleUrl.replace(
                    '{platform}',
                    platform === 'windows' ? 'win-x64' : 'linux-x64'
                  )}
                >
                  agent bundle ({platform === 'windows' ? 'Windows' : 'Linux'})
                </a>
                .{' '}
              </>
            )}
            <Link
              className="nyk-link"
              to={guidePath(`agent-${platform}`, {
                clientId: agent?.clientId,
                gatewayUrl: env.gatewayWsUrl,
                bundleWin: env.agentBundleUrl?.replace('{platform}', 'win-x64'),
                bundleLinux: env.agentBundleUrl?.replace('{platform}', 'linux-x64'),
                agentImage: env.agentImage,
              })}
            >
              Installation guide
            </Link>
          </p>
          <FileOutput files={files} zipName={`agent-${agent?.clientId ?? ''}.zip`} />
          {connected ? (
            <Note tone="good">
              {agent?.clientId} is connected{tunnel?.machineName ? ` from ${tunnel.machineName}` : ''}.
            </Note>
          ) : (
            <Note tone="warn">Waiting for {agent?.clientId} to connect (checked every 10 s).</Note>
          )}
        </>
      ) : (
        <Note tone="warn">This environment has no automation gateway URL configured.</Note>
      ),
    },
    {
      id: 'move-to-auth',
      title: 'Move to AUTH',
      state: !agent
        ? 'blocked'
        : agent.authMode === 'auth'
          ? 'done'
          : agent.authClientId && agent.authMode === 'both'
            ? 'waiting'
            : 'optional',
      blocked: 'Create the agent first.',
      summary: agent?.authMode ? `mode ${agent.authMode}` : undefined,
      body: agent ? (
        <NeedsSession target={target}>
          <Facts
            rows={[
              ['Mode', agent.authMode ?? 'legacy'],
              ['AUTH client', agent.authClientId ?? 'none'],
              ['Last AUTH sign-in', agent.lastBearerAt ? formatRelative(agent.lastBearerAt) : '—'],
              ['Last error', agent.lastAuthError ?? '—'],
            ]}
          />
          <p>
            {platform === 'container'
              ? 'A container agent got its AUTH client with its config; allow AUTH, then require it once it signs in with it.'
              : 'An installed agent gets its AUTH client over its connection (it must be online), then allow AUTH and require it once it signs in with it.'}
          </p>
          <StepActions error={action.error}>
            {platform !== 'container' && !agent.authClientId && (
              <Button
                disabled={!connected || !linked}
                title={
                  !connected
                    ? 'The agent must be connected.'
                    : !linked
                      ? 'Link the tenant to AUTH first.'
                      : undefined
                }
                loading={action.busy === 'move'}
                onClick={() => void action.run('move', () => gw.moveToAuth(agentId))}
              >
                Move to AUTH
              </Button>
            )}
            {(['both', 'auth'] as AuthMode[])
              .filter(
                (m) => m !== agent.authMode && (m === 'both' || !!agent.lastBearerAt) && !!agent.authClientId
              )
              .map((m) => (
                <Button
                  key={m}
                  variant="ghost"
                  loading={action.busy === `mode-${m}`}
                  onClick={() => void action.run(`mode-${m}`, () => gw.setAuthMode(agentId, m))}
                >
                  {m === 'both' ? 'Allow AUTH (both)' : 'Require AUTH'}
                </Button>
              ))}
          </StepActions>
        </NeedsSession>
      ) : null,
    },
  ];

  return (
    <Wizard
      steps={steps}
      loading={state.loading && !s}
      error={state.error}
      onRecheck={() => void state.reload()}
    />
  );
}
